import { useRef, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Import for HEIC → JPEG in the browser
import convert from 'heic-convert/browser';
import './FileUploader.css';

// map extensions → MIME (include both MIME and .ext tokens for best cross-browser support)
const MIME_BY_EXT = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic', // Safari/iOS
  heif: 'image/heif',
  gif: 'image/gif',
  avif: 'image/avif',
  pdf: 'application/pdf',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  webm: 'video/webm',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv', // add 'application/csv' as a fallback if you want
};

const FileUploader = ({
  isImageUpload = false,
  multiple = false,
  maxFileSizeMB = 10,
  maxImageSizeMB = 1,
  allFormatsAllowed = true,
  allowedFormats = ['jpg', 'jpeg', 'png', 'pdf', 'webp', 'heic', 'pdf', 'mp4', 'webm', 'mov'],
  onFilesChange,
  clearUploader,
  setClearUploader,
}) => {
  const fileInputRef = useRef(null);
  const [filesPreview, setFilesPreview] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  // Build accept list dynamically from allowedFormats
  const accept = useMemo(() => {
    if (allFormatsAllowed || !Array.isArray(allowedFormats) || allowedFormats.length === 0) {
      return undefined; // show everything
    }
    const tokens = new Set();
    for (const extRaw of allowedFormats) {
      if (!extRaw) continue;
      const ext = String(extRaw).toLowerCase().replace(/^\./, '');
      const mime = MIME_BY_EXT[ext];
      if (mime) tokens.add(mime);
      tokens.add(`.${ext}`);
      // heic quirk: some browsers use heif
      if (ext === 'heic') tokens.add('image/heif');
    }
    return Array.from(tokens).join(',');
  }, [allowedFormats, allFormatsAllowed]);

  useEffect(() => {
    if (clearUploader) {
      setFilesPreview([]);
      onFilesChange([]);
      setClearUploader(false);
    }
  }, [clearUploader, onFilesChange, setClearUploader]);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    setErrorMessage('');
    const chosenFiles = Array.from(e.target.files);
    if (!chosenFiles.length) return;

    // If multiple is false, replace the entire list; otherwise, append.
    // Start with either an empty list or whatever we had before.
    const finalFiles = multiple ? [...filesPreview] : [];

    for (const rawFile of chosenFiles) {
      // Extract extension and size
      const ext = rawFile.name.split('.').pop().toLowerCase();
      const sizeMB = rawFile.size / (1024 * 1024);

      // Extension check (if not all allowed)
      if (!allFormatsAllowed && !allowedFormats.includes(ext)) {
        setErrorMessage(`File type .${ext} is not allowed.`);
        continue;
      }

      // Size check
      const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif', 'avif']);
      const isLikelyImage = IMAGE_EXTS.has(ext);
      if (isLikelyImage) {
        if (sizeMB > maxImageSizeMB) {
          setErrorMessage(
            `Image "${rawFile.name}" exceeds the max image size of ${maxImageSizeMB}MB.`,
          );
          continue;
        }
      } else {
        if (sizeMB > maxFileSizeMB) {
          setErrorMessage(
            `File "${rawFile.name}" exceeds the max file size of ${maxFileSizeMB}MB.`,
          );
          continue;
        }
      }

      // STEP 1: Create a placeholder entry and immediately show it
      const placeholder = {
        file: null, // We'll fill this in after reading
        name: rawFile.name,
        sizeMB: sizeMB.toFixed(2),
        type: rawFile.type || '',
        extension: ext,
        base64Preview: null,
        loading: true, // <-- Mark as loading
        base64File: null,
      };
      finalFiles.push(placeholder);
      setFilesPreview([...finalFiles]);
      // This re-renders, so the user sees a "loading" preview box immediately.

      // STEP 2: Process the file asynchronously (convert/read).
      try {
        let finalFile = rawFile;
        let previewExtension = ext;
        let base64Preview = null;
        let base64 = null;

        if (ext === 'heic') {
          // HEIC -> JPEG
          const inputBuffer = new Uint8Array(await rawFile.arrayBuffer());
          const outputBuffer = await convert({
            buffer: inputBuffer,
            format: 'JPEG',
            quality: 1,
          });

          // Create a JPEG File
          const newBlob = new Blob([outputBuffer], { type: 'image/jpeg' });
          finalFile = new File([newBlob], rawFile.name.replace(/\.heic$/, '.jpg'), {
            type: 'image/jpeg',
          });

          previewExtension = 'jpg';
          base64Preview = await arrayBufferToBase64(outputBuffer, 'image/jpeg');
        } else if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
          // Normal image preview
          base64Preview = await fileToBase64(finalFile);
        } else if (['pdf', 'pdf', 'mp4', 'webm', 'mov'].includes(ext)) {
          base64 = await fileToBase64(finalFile);
        }

        // STEP 3: Update the placeholder entry with final data
        placeholder.file = finalFile;
        placeholder.name = finalFile.name;
        placeholder.extension = previewExtension;
        placeholder.type = finalFile.type;
        placeholder.base64Preview = base64Preview;
        placeholder.base64File = base64;
        placeholder.loading = false; // Done loading
        // Re-render with updated info
        setFilesPreview([...finalFiles]);
      } catch (err) {
        console.error('Error during file conversion/reading:', err);
        setErrorMessage(`Failed to process "${rawFile.name}".`);
        // We can optionally remove the placeholder or mark it as errored
        // For now, let's just set loading to false so we show something
        placeholder.loading = false;
        setFilesPreview([...finalFiles]);
      }
    }

    // Finally, let the parent know about final files
    onFilesChange(finalFiles);
  };

  // Convert File to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Convert array buffer to base64
  const arrayBufferToBase64 = (buffer, mimeType = 'image/jpeg') => {
    return new Promise((resolve) => {
      const blob = new Blob([buffer], { type: mimeType });
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const handleRemoveFile = (index) => {
    const updated = [...filesPreview];
    updated.splice(index, 1);
    setFilesPreview(updated);
    onFilesChange(updated);
  };

  return (
    <>
      {/* Previews */}
      {filesPreview.length > 0 && (
        <AnimatePresence>
          <div className="files-preview">
            {filesPreview.map((item, idx) => {
              if (item.loading) {
                // Show a loading spinner or skeleton
                return (
                  <motion.div
                    className="file-preview-item loading"
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.3 }}
                  >
                    <div className="file-loader">
                      {/* Example spinner styling or an actual spinner icon */}
                      <div className="loader" />
                    </div>

                    <button
                      className="remove-file-button"
                      onClick={() => handleRemoveFile(idx)}
                    ></button>
                  </motion.div>
                );
              }

              // If base64Preview is present, it’s an image
              const isImage = !!item.base64Preview;
              return (
                <motion.div
                  className={`file-preview-item ${!isImage ? 'file' : ''} ${
                    !isImage ? `file-${item.extension}` : ''
                  }`}
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.3 }}
                >
                  {isImage ? (
                    <div className="image-container">
                      <img src={item.base64Preview} alt={item.name} className="image-preview" />
                    </div>
                  ) : (
                    <div className="file-wrapper">
                      <div className="file-icon"></div>
                      <div className="file-info">
                        <div className="file-name">{item.name}</div>
                        <div className="file-creds">
                          <span className="file-ext">{item.extension}</span>
                          <span>Document</span>
                          <span className="file-separator"></span>
                          <span className="file-size">{item.sizeMB}</span>
                          <span>MB</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    className="remove-file-button"
                    onClick={() => handleRemoveFile(idx)}
                  ></button>
                </motion.div>
              );
            })}
          </div>
        </AnimatePresence>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="file-upload-error">
          <div>{errorMessage}</div>
          <button className="close-error-button" onClick={() => setErrorMessage('')}></button>
        </div>
      )}

      {/* Upload Button */}
      <div className={`file-uploader-container ${filesPreview.length > 0 ? 'file-uploaded' : ''}`}>
        {/* Tooltip Info */}

        <AnimatePresence>
          {showTooltip && (
            <motion.div
              className="file-perm-popup"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              {isImageUpload ? (
                <>
                  <div className="formats-title">Max image size: {maxImageSizeMB} MB</div>
                  <div className="formats-list">
                    {allowedFormats.map((format, index) => (
                      <span key={index}>{format.toUpperCase()}</span>
                    ))}
                  </div>
                </>
              ) : allFormatsAllowed ? (
                <>
                  <div className="formats-title">Max file size: {maxFileSizeMB}MB</div>
                </>
              ) : (
                <>
                  <div className="formats-title">Max file size: {maxFileSizeMB}MB</div>
                  <div className="formats-list">
                    {allowedFormats.map((format, index) => (
                      <span key={index}>{format.toUpperCase()}</span>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <button
          className={`attach-file-button ${isImageUpload ? 'image-uploader' : ''}`}
          onClick={handleClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {isImageUpload && <span className="attach-mask-icon" aria-hidden="true" />}
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          multiple={multiple}
          accept={accept}
          onChange={handleFileChange}
        />
      </div>
    </>
  );
};

export default FileUploader;
